import { DetailSkeleton } from 'components/skeletons';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { Box, Button, CircularProgress, Tooltip, Typography } from '@mui/material';
import { DoNotDisturbOnOutlined, LockOpenOutlined } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';

import { Form } from './form';
import { CloseAgencyDialog, DEFAULT_CLOSING_MESSAGE } from './close.dialog';
import { FormProvider, useForm } from 'react-hook-form';
import { useCloseAgency, useGetAgency, useReopenAgency, useUpdateAgency } from 'core/agencies/hooks';
import { useUserSettingsStateContext } from 'core/user.settings/hooks';
import { CardHeader } from 'components/cards/header';
import { AxiosError } from 'axios';

type Props = {
    agencyData: any;
};

const ClosedBanner = ({agencyData, canReopen, onReopen, loading}: {agencyData: any; canReopen: boolean; onReopen: () => void; loading: boolean}) => (
    <Box sx={{ mx: '1.5rem', mb: '0.5rem', p: 2, borderRadius: '12px', bgcolor: '#FFF4E5', border: '1px solid #FFE0B2', display: 'flex', gap: 2, alignItems: 'flex-start' }}>
        <DoNotDisturbOnOutlined sx={{ color: '#B26A00', mt: '2px' }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontWeight: 600, color: '#15161A' }}>
                Agence fermée le {dayjs(agencyData.closed_at).format('DD/MM/YYYY à HH:mm')}
            </Typography>
            <Typography variant='body2' sx={{ color: '#3E434A', mt: 0.5 }}>
                {agencyData.temp_workers_count} intérimaire{agencyData.temp_workers_count > 1 ? 's' : ''} toujours rattaché{agencyData.temp_workers_count > 1 ? 's' : ''}.
                Les collaborateurs ne voient plus cette agence et les nouveaux inscrits sont dirigés vers l'agence ouverte la plus proche.
            </Typography>
            <Typography sx={{ mt: 1.5, fontSize: '0.6875rem', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'text.secondary' }}>
                Message affiché aux intérimaires
            </Typography>
            <Typography variant='body2' sx={{ color: '#3E434A', whiteSpace: 'pre-wrap', fontStyle: agencyData.closing_message ? 'normal' : 'italic' }}>
                {agencyData.closing_message || DEFAULT_CLOSING_MESSAGE(agencyData.name)}
            </Typography>
        </Box>
        {canReopen && (
            <Button variant='outlined' color='warning' size='small' startIcon={loading ? <CircularProgress size={12} /> : <LockOpenOutlined />} onClick={onReopen} disabled={loading} sx={{ flexShrink: 0 }}>
                Rouvrir l'agence
            </Button>
        )}
    </Box>
);

const EditForm = ({agencyData}: Props) => {
    const navigate = useNavigate();
    const {user: userData} = useUserSettingsStateContext();
    const isAdmin = JSON.parse(userData || '{}').role === 'admin';
    const isClosed = Boolean(agencyData.closed);
    const [closeDialogOpen, setCloseDialogOpen] = useState(false);

    // Form
    const methods = useForm({mode: 'onChange', defaultValues: useMemo(() => agencyData, [agencyData])});
    const { formState: { errors, dirtyFields }, reset, setError } = methods;
    const onFormSubmit = (data: any) => mutation.mutate({id: agencyData.id, data: data});
    const onReset = useCallback(() => reset(agencyData), [agencyData, reset]);
    useEffect(() => reset(agencyData), [agencyData, reset]);

    // Mutation
    const mutation = useUpdateAgency({
        onError: (error) => {
            toast.error("Une Erreur est survenue");

            if (error instanceof AxiosError) {
                const {full_messages, ...errors} = error.response?.data?.errors ?? {};
                Object.entries<Array<string>>(errors).forEach(([key, value]) => {
                    if (!['base'].includes(key)) { setError(key, {type: 'custom', message: value.join(', ')}); }
                });
            }
        },
        onSuccess: (data: any) => {
            toast.success("L'agence a été modifié");
            navigate(`/dashboard/agencies/${data.id}`);
        }
    });

    const closeMutation = useCloseAgency({
        onError: (error) => {
            const baseErrors = error instanceof AxiosError ? error.response?.data?.errors?.base : undefined;
            toast.error(Array.isArray(baseErrors) && baseErrors.length > 0 ? baseErrors.join(', ') : "Une erreur est survenue");
        },
        onSuccess: () => {
            setCloseDialogOpen(false);
            toast.success("L'agence a été fermée");
        }
    });

    const reopenMutation = useReopenAgency({
        onError: () => toast.error("Une erreur est survenue"),
        onSuccess: () => toast.success("L'agence a été rouverte"),
    });

    return (
        <Box component='main'>
            <FormProvider {...methods}>
                <Box component='form' onSubmit={methods.handleSubmit(onFormSubmit)} noValidate>
                    <CardHeader
                        title={`${agencyData.name}`}
                        chips={isClosed ? [{label: 'Fermée', color: 'error'}] : undefined}
                        rightComponent={(
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', my: 3, gap: '1rem' }}>
                                {isAdmin && !isClosed && (
                                    <Button variant='outlined' color='error' startIcon={<DoNotDisturbOnOutlined />} onClick={() => setCloseDialogOpen(true)} disabled={mutation.isLoading}>
                                        Fermer l'agence
                                    </Button>
                                )}
                                {!isClosed && (
                                    <>
                                        <Button variant='contained' color='secondary' onClick={onReset} disabled={mutation.isLoading || Object.keys(dirtyFields).length === 0}>
                                            Annuler
                                        </Button>
                                        <Tooltip title={Object.keys(errors).length > 0 ? 'Veuillez fixer les erreurs' : (Object.keys(dirtyFields).length === 0 ? 'Aucun champs a été modifié' : "")}>
                                            <span>
                                                <Button type='submit' variant='contained' color='primary' sx={{ color: 'white' }} disabled={mutation.isLoading || Object.keys(errors).length > 0 || Object.keys(dirtyFields).length === 0}>
                                                    {mutation.isLoading && <CircularProgress size={10} />}
                                                    Sauvegarder
                                                </Button>
                                            </span>
                                        </Tooltip>
                                    </>
                                )}
                            </Box>
                        )}
                    />
                    {isClosed && (
                        <ClosedBanner
                            agencyData={agencyData}
                            canReopen={isAdmin}
                            loading={reopenMutation.isLoading}
                            onReopen={() => reopenMutation.mutate({id: agencyData.id})}
                        />
                    )}
                    <Form disabled={isClosed} />
                </Box>
            </FormProvider>
            <CloseAgencyDialog
                open={closeDialogOpen}
                agencyName={agencyData.name}
                tempWorkersCount={agencyData.temp_workers_count ?? 0}
                loading={closeMutation.isLoading}
                onClose={() => setCloseDialogOpen(false)}
                onConfirm={(message) => closeMutation.mutate({id: agencyData.id, closing_message: message})}
            />
        </Box>
    );
};

const EditPage = () => {
    const {agencyId} = useParams();
    const {data: agencyData, isLoading} = useGetAgency(Number(agencyId!), {onError: (error: any) => toast.error("Une erreur est survenue.")});

    return (
        <Box>
            {agencyData ? (<EditForm agencyData={agencyData} />) : isLoading ? (<DetailSkeleton />) : (<Typography>Une erreur est survenue</Typography>)}
        </Box>
    );
};

export { EditPage };
